import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MutableRefObject, useMemo, useRef } from 'react'

import { HydratedTransaction } from 'pages/transactions/util/TransactionsTypes'
import { convertTransactionNumbers } from 'pages/transactions/util/TransactionsUtils'

import {
  AssetQuantity,
  FetchPaginatedTransactionsResponse,
  Transaction,
  TransactionAnnotationUpdates,
} from 'api/types/transaction/transactionApiTypes'
import useApi from 'api/useApi'
import { EXTERNAL } from 'constants/TransactionTypes'
import { useBackendFilters } from 'contexts/filters/BackendFiltersContext'
import { useOptimisticPendingChanges } from 'hooks/data/gc-dags/useOptimisticPendingChanges'
import { getOverriddenConvertedValues } from 'hooks/data/getOverriddenConvertedValues'
import { QueryKey } from 'hooks/data/queryKeys'
import { useOrgSetting } from 'hooks/data/settingsHooks'
import { getApiFilterParams } from 'hooks/utils/getApiFilterParams'
import useAppStore from 'store/useAppStore'

import { useOrgQueryKey } from './utils/useOrgQueryKey'

export const useTransactionFilterParams = (ref?: MutableRefObject<undefined | string>) => {
  const { filterState } = useBackendFilters()

  if (ref) {
    ref.current = filterState.queryExecutionId
  }

  return useMemo(
    () => ({
      ...getApiFilterParams(filterState),
      next_token: filterState.currentToken,
      include_accounting_status: false,
    }),
    [filterState]
  )
}

const useGetTransactionsQueryParams = (ref: MutableRefObject<undefined | string>) => {
  const params = useTransactionFilterParams(ref)
  const paginatedTransactionsQueryKey = useOrgQueryKey(QueryKey.PaginatedTransactions)

  return [...paginatedTransactionsQueryKey, { ...params }]
}

export function usePaginatedTransactions() {
  // Why are query execution IDs being stored in refs?
  // It is used to fetch the next set of transactions when paginating.
  // However we don't want to trigger a re-fetch of the transactions when the query execution ID changes.
  // So we store the query execution ID in a ref and pass it to the useTransactionFilterParams hook.
  // This way the query execution ID is not a dependency of the useQuery hook and the query will not re-fetch when the query execution ID changes.
  // It will always change after the first fetch, so we don't want to trigger a re-fetch.
  const refQueryExecutionId = useRef(undefined)
  const { fetch_paginated_transactions } = useApi()
  const explode = useOrgSetting('explode_groups_experimental')

  const params = useTransactionFilterParams(refQueryExecutionId)
  const queryParams = useGetTransactionsQueryParams(refQueryExecutionId)

  const { data, isLoading, isFetching, ...restQuery } = useQuery(queryParams, () =>
    fetch_paginated_transactions({
      ...params,
      query_execution_id: refQueryExecutionId.current,
    })
  )

  const transformedData = useMemo(() => {
    const transactions: Transaction[] = []
    const mainTxns: Transaction[] = []
    const brokenUpParents: Record<string, boolean> = {}
    const parentToGroup: Record<string, string> = {}
    const groups: Record<string, Transaction[]> = {}

    if (isLoading || isFetching) {
      return { transactions, transactionsGrouped: mainTxns, brokenUpParents }
    }

    // First pass: keep every leg and bucket it by groupId (encounter order).
    for (const _txn of data?.transactions || []) {
      // Convert all the numbers to numbers
      const txn = convertTransactionNumbers(_txn)

      if (explode.value) {
        txn.groupId = txn.id
        txn.groupTransactionType = txn.transactionType

        if (txn.transactionType === EXTERNAL) {
          continue
        }
      }

      transactions.push(txn)
      ;(groups[txn.groupId] ??= []).push(txn)

      if (parentToGroup[txn.parentId]) {
        if (parentToGroup[txn.parentId] !== txn.groupId) {
          brokenUpParents[txn.parentId] = true
        }
      } else {
        parentToGroup[txn.parentId] = txn.groupId
      }
    }

    // Pick the "main" leg of each group from the backend `isMain` flag. (The
    // deprecated `id === groupId` heuristic breaks under Atlas, where every leg
    // shares the group hash as its `id`.) If a group has no isMain leg (e.g.
    // not yet backfilled), fall back to the smallest-versionId leg so the group
    // still renders and no transactions disappear. In explode mode each leg is
    // its own group, so it is its own main.
    const mainLegs = new Set<Transaction>()
    for (const legs of Object.values(groups)) {
      const main =
        legs.find((leg) => leg.isMain) ?? legs.reduce((a, b) => (b.versionId < a.versionId ? b : a))
      mainLegs.add(main)
    }

    // Second pass: emit mains in encounter order, nesting the rest under each.
    for (const txn of transactions) {
      if (!mainLegs.has(txn)) {
        continue
      }
      mainTxns.push(txn)
      txn.group = groups[txn.groupId].filter((leg) => leg !== txn)
    }

    return { transactions, transactionsGrouped: mainTxns, brokenUpParents }
  }, [data?.transactions, explode.value, isFetching, isLoading])

  return {
    ...restQuery,
    isLoading: isLoading || isFetching,
    result: {
      txnData: transformedData,
      nextToken: data?.next_token ?? undefined,
      queryExecutionId: data?.query_execution_id ?? undefined,
    },
  }
}

export function usePaginatedTransactionsMutation() {
  const refQueryExecutionId = useRef(undefined)
  const { save_transaction_edits } = useApi()
  const queryClient = useQueryClient()
  const queryParams = useGetTransactionsQueryParams(refQueryExecutionId)
  const { setPendingChangeFlag } = useOptimisticPendingChanges()

  const saveTxnEdits = (editData: {
    txns: Transaction[] | HydratedTransaction[]
    annotationUpdates: TransactionAnnotationUpdates
    nestedTxnEdit?: boolean
  }) => {
    // Expand transactions to include grouped transactions when needed
    const allTxnsToEdit = editData.txns.flatMap((groupedTxn) => {
      return groupedTxn.group && !editData.nestedTxnEdit
        ? [...groupedTxn.group, groupedTxn]
        : [groupedTxn]
    })

    const editedTransactions = allTxnsToEdit.map((txn) => {
      return {
        transaction_id: txn.id, // redundant, but harmless — backend doesn't key on it
        transaction_date: txn.timestamp.slice(0, 10),
        version_id: txn.versionId, // ← the actual identifier the backend uses
      }
    })

    // Filter out empty category before sending to backend
    const filteredAnnotationUpdates = { ...editData.annotationUpdates }

    if (filteredAnnotationUpdates.category === '') {
      delete filteredAnnotationUpdates.category
    }

    return save_transaction_edits(editedTransactions, filteredAnnotationUpdates)
  }

  return useMutation(saveTxnEdits, {
    onMutate: async ({ txns, annotationUpdates, nestedTxnEdit }) => {
      await queryClient.cancelQueries(queryParams)

      const rollbackResponse = queryClient.getQueryData(queryParams)

      // Apply the same expansion logic as in saveTxnEdits
      const allTxnsToEdit = txns.flatMap((groupedTxn) => {
        return groupedTxn.group && !nestedTxnEdit ? [...groupedTxn.group, groupedTxn] : [groupedTxn]
      })

      queryClient.setQueryData<FetchPaginatedTransactionsResponse>(
        queryParams,
        (existingResponse) => {
          if (existingResponse) {
            const existingTxns = existingResponse.transactions
            // Identify edited legs by versionId
            const versionIdsToEdit = new Set(allTxnsToEdit.map((txn) => txn.versionId))

            const editedTxns = existingTxns.map((txn) => {
              if (versionIdsToEdit.has(txn.versionId)) {
                // Filter out empty category for optimistic UI update (keep existing category)
                const optimisticUpdates = { ...annotationUpdates }
                if (optimisticUpdates.category === '') {
                  delete optimisticUpdates.category
                }

                const updatedTxn = {
                  ...txn,
                  annotation: {
                    ...txn.annotation,
                    ...optimisticUpdates,
                  },
                }
                if (annotationUpdates.custom_fields) {
                  const changedFields = JSON.parse(annotationUpdates.custom_fields) as Record<
                    string,
                    string | null
                  >

                  // Start with existing custom fields and merge in the changes
                  const existingFields = new Map(
                    txn.customFields.map((cf) => [cf.field_id, cf.value])
                  )

                  // Update/add the changed fields
                  for (const [field_id, value] of Object.entries(changedFields)) {
                    existingFields.set(field_id, value ?? '')
                  }

                  // Convert back to array format
                  updatedTxn.customFields = [...existingFields.entries()].map(
                    ([field_id, value]) => ({
                      field_id,
                      value,
                    })
                  )
                }
                if (annotationUpdates.fiatOverride) {
                  const fiatOverride = JSON.parse(annotationUpdates.fiatOverride) as AssetQuantity
                  const convertedValuesOverride = {
                    asset: fiatOverride.asset,
                    value: fiatOverride.amount,
                  }
                  updatedTxn.value.convertedValues = getOverriddenConvertedValues(
                    txn,
                    convertedValuesOverride
                  )
                }
                return updatedTxn
              } else {
                return txn
              }
            })

            return {
              ...existingResponse,
              transactions: editedTxns,
            }
          } else {
            return undefined
          }
        }
      )

      return rollbackResponse
    },
    onSuccess: () => {
      void queryClient.invalidateQueries([QueryKey.HasGlobalEdits])
      setPendingChangeFlag('txn_update')
    },
    onError: (_error, _response, rollbackResponse) => {
      queryClient.setQueryData(queryParams, rollbackResponse)
    },
  })
}

// used when we want to edit all transactions that match the current filter object
export function useSaveTransactionEditsByFilters() {
  const { save_select_all_transaction_edits } = useApi()
  const filters = useTransactionFilterParams()
  const addCosmicToast = useAppStore((store) => store.addCosmicToast)
  const { setPendingChangeFlag } = useOptimisticPendingChanges()

  const saveTxnEdits = ({
    annotationUpdates,
  }: {
    annotationUpdates: TransactionAnnotationUpdates
  }) => {
    // Filter out empty category before sending to backend
    const filteredAnnotationUpdates = { ...annotationUpdates }

    if (filteredAnnotationUpdates.category === '') {
      delete filteredAnnotationUpdates.category
    }

    return save_select_all_transaction_edits(filteredAnnotationUpdates, filters)
  }

  return useMutation(saveTxnEdits, {
    onError: () => {
      addCosmicToast({
        variant: 'danger',
        message:
          'Failed to bulk edit. Try again and please contact us at help@taxbit.com if this issue persists.',
      })
    },
    onSuccess: () => {
      addCosmicToast({
        variant: 'primary',
        message:
          'Bulk edit succeeded. Your bulk edit has successfully been processed. Please refresh to see your changes.',
      })
      setPendingChangeFlag('txn_update')
    },
  })
}
